<script setup lang="ts">
import type { Car } from '../internal/types.ts'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'
import CarPhoto from '@/features/garage/components/CarPhoto.vue'
import CarStatusBadge from '@/features/garage/components/CarStatusBadge.vue'
import { carSubtitle } from '../internal/car-presentation.ts'

const props = defineProps<{
  cars: readonly Car[]
  photoUrls: ReadonlyMap<string, string>
  currentId: string | undefined
  canManage: boolean
}>()
defineEmits<{ setPrimary: [carId: string] }>()

const { t } = useI18n()
</script>

<template>
  <nav :aria-label="t('garage.selector.label')">
    <ul class="flex flex-col gap-1">
      <li
        v-for="car in props.cars"
        :key="car.id"
        class="flex items-stretch gap-2 rounded-[var(--radius-surface)] border-l-2"
        :class="
          car.id === props.currentId
            ? 'border-[color:var(--color-accent)] bg-[color:color-mix(in_srgb,var(--color-accent)_10%,transparent)]'
            : 'border-transparent hover:bg-[color:var(--color-surface-raised)]'
        "
      >
        <RouterLink
          :to="{ name: 'garage-car-detail', params: { carId: car.id } }"
          :aria-current="car.id === props.currentId ? 'page' : undefined"
          class="flex min-w-0 flex-1 items-center gap-3 rounded-[var(--radius-surface)] p-3 text-[color:var(--color-text)]"
        >
          <CarPhoto :src="props.photoUrls.get(car.id)" alt="" size="compact" />
          <span class="flex min-w-0 flex-col gap-1">
            <span class="truncate font-semibold">{{ car.name }}</span>
            <span v-if="carSubtitle(car) !== undefined" class="truncate text-[color:var(--color-text-muted)]">
              {{ carSubtitle(car) }}
            </span>
            <span><CarStatusBadge :status="car.status" /></span>
          </span>
        </RouterLink>
        <span
          v-if="car.isPrimary"
          class="flex items-start p-3 text-[color:var(--color-accent)]"
          :title="t('garage.primary')"
        >
          <span aria-hidden="true">★</span>
          <span class="sr-only">{{ t('garage.primary') }}</span>
        </span>
        <button
          v-else-if="props.canManage && car.status === 'ACTIVE'"
          type="button"
          class="flex min-h-[var(--density-target-min)] min-w-[var(--density-target-min)] items-start justify-center p-3 text-[color:var(--color-text-muted)] hover:text-[color:var(--color-accent)]"
          :title="t('garage.setPrimary')"
          :aria-label="`${t('garage.setPrimary')}: ${car.name}`"
          @click="$emit('setPrimary', car.id)"
        >
          <span aria-hidden="true">☆</span>
        </button>
      </li>
    </ul>
  </nav>
</template>
